/**
 * Custom element that manages a "products per page" dropdown.
 *
 * Persists the selection via Shopify cart attributes (server-readable by Liquid)
 * and localStorage (survives cart clearing after checkout).
 */
class PerPageSelector extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select) return;

    this.select.addEventListener('change', this.#handleChange);
    this.#syncPreference();
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.#handleChange);
  }

  /**
   * On page load, restore a saved preference that may have been lost
   * (e.g. after checkout clears the cart). Uses a sessionStorage flag
   * to prevent infinite reload loops if the sync fails.
   */
  async #syncPreference() {
    const stored = localStorage.getItem('products_per_page');
    const current = this.select.value;

    if (stored === current) {
      sessionStorage.removeItem('per_page_sync_attempted');
      return;
    }

    if (!stored) {
      localStorage.setItem('products_per_page', current);
      return;
    }

    if (sessionStorage.getItem('per_page_sync_attempted')) {
      sessionStorage.removeItem('per_page_sync_attempted');
      localStorage.setItem('products_per_page', current);
      return;
    }

    try {
      sessionStorage.setItem('per_page_sync_attempted', '1');
      await this.#updateCartAttribute(stored);
      this.#navigateToFirstPage();
    } catch {
      sessionStorage.removeItem('per_page_sync_attempted');
      localStorage.setItem('products_per_page', current);
    }
  }

  #handleChange = async () => {
    const perPage = this.select.value;
    localStorage.setItem('products_per_page', perPage);
    this.select.disabled = true;

    try {
      await this.#updateCartAttribute(perPage);
      this.#navigateToFirstPage();
    } catch (error) {
      console.error('Failed to update products-per-page preference:', error);
      this.select.disabled = false;
    }
  };

  #updateCartAttribute(perPage) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { per_page: perPage } }),
    });
  }

  #navigateToFirstPage() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }
}

if (!customElements.get('per-page-selector')) {
  customElements.define('per-page-selector', PerPageSelector);
}
